import React, { useState, useEffect, useMemo } from 'react';
import { tournamentService } from '../../services/tournamentService';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  IconButton,
  Chip,
  Collapse,
  Paper
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TournamentDetailModal from './TournamentDetailModal';

const TournamentList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  
  const [tournaments, setTournaments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState({});

  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        const data = await tournamentService.getAllTournaments();
        
        if (!Array.isArray(data)) {
          throw new Error('データの形式が不正です');
        }
        setTournaments(data);
        setError(null);
        
        // 現在の月を自動的に展開
        const currentMonth = new Date().toISOString().slice(0, 7);
        setExpandedMonths({ [currentMonth]: true });
      } catch (err) {
        console.error('トーナメント一覧の取得に失敗:', err);
        setError(`トーナメントの取得に失敗しました。${err.message || ''}`);
        setTournaments(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTournaments();
  }, []);

  // 過去の大会かどうかを判定
  const isPastTournament = (date) => {
    if (!date) return false;
    const tournamentDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return tournamentDate < today;
  };

  // 月ごとにグループ化
  const groupedTournaments = useMemo(() => {
    if (!Array.isArray(tournaments)) return {};
    
    const grouped = {};
    tournaments.forEach(tournament => {
      if (!tournament.date) return;
      
      const monthKey = tournament.date.slice(0, 7); // YYYY-MM形式
      if (!grouped[monthKey]) {
        grouped[monthKey] = [];
      }
      grouped[monthKey].push(tournament);
    });
    
    // 各月のトーナメントを日付順にソート
    Object.keys(grouped).forEach(month => {
      grouped[month].sort((a, b) => new Date(a.date) - new Date(b.date));
    });
    
    return grouped;
  }, [tournaments]);

  // 月の表示名を取得
  const getMonthDisplayName = (monthKey) => {
    const [year, month] = monthKey.split('-');
    return `${year}年${parseInt(month)}月`;
  };

  // 月の展開/折りたたみを切り替え
  const toggleMonth = (monthKey) => {
    setExpandedMonths(prev => ({
      ...prev,
      [monthKey]: !prev[monthKey]
    }));
  };

  const columns = [
    {
      field: 'title',
      headerName: '大会名',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => {
        if (!params?.row) return '';
        return params.row.tournamentName || params.row.title || '';
      },
      renderCell: (params) => {
        const value = params.row?.tournamentName || params.row?.title || '';
        const isPast = isPastTournament(params.row?.date);
        return (
          <Typography 
            noWrap 
            title={value}
            sx={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: isPast ? 'rgba(255, 255, 255, 0.5)' : '#ffffff',
            }}
          >
            {value || '(タイトルなし)'}
          </Typography>
        );
      }
    },
    {
      field: 'date',
      headerName: '開催日',
      width: 110,
      valueGetter: (params) => params?.row?.date || '',
      renderCell: (params) => {
        const date = params.row?.date;
        const isPast = isPastTournament(date);
        if (!date) return '';
        
        try {
          const dateObj = new Date(date);
          const month = dateObj.getMonth() + 1;
          const day = dateObj.getDate();
          const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
          const weekday = weekdays[dateObj.getDay()];
          
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography noWrap sx={{ 
                fontSize: '0.875rem',
                color: isPast ? 'rgba(255, 255, 255, 0.5)' : '#ffffff',
              }}>
                {`${month}/${day}(${weekday})`}
              </Typography>
              {isPast && (
                <Chip 
                  label="終了" 
                  size="small" 
                  sx={{ 
                    height: 20,
                    fontSize: '0.7rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  }} 
                />
              )}
            </Box>
          );
        } catch (error) {
          return date;
        }
      }
    },
    {
      field: 'time',
      headerName: '時間',
      width: 120,
      valueGetter: (params) => params?.row?.time || '',
      renderCell: (params) => {
        const isPast = isPastTournament(params.row?.date);
        return (
          <Typography noWrap sx={{ 
            fontSize: '0.875rem',
            color: isPast ? 'rgba(255, 255, 255, 0.5)' : '#ffffff',
          }}>
            {params.value || '-'}
          </Typography>
        );
      }
    },
    {
      field: 'category',
      headerName: 'カテゴリ',
      width: 140,
      valueGetter: (params) => params?.row?.category || '',
      renderCell: (params) => {
        const isPast = isPastTournament(params.row?.date);
        return (
          <Typography noWrap sx={{ 
            fontSize: '0.875rem',
            color: isPast ? 'rgba(255, 255, 255, 0.5)' : '#ffffff',
          }}>
            {params.value || '-'}
          </Typography>
        );
      }
    },
    {
      field: 'venue',
      headerName: '会場',
      width: 150,
      valueGetter: (params) => params?.row?.venue || '',
      renderCell: (params) => {
        const venue = params.row?.venue;
        const isPast = isPastTournament(params.row?.date);
        return (
          <Typography 
            noWrap 
            title={venue}
            sx={{ 
              fontSize: '0.875rem',
              color: isPast ? 'rgba(255, 255, 255, 0.5)' : '#ffffff',
            }}
          >
            {venue || '-'}
          </Typography>
        );
      }
    },
    {
      field: 'actions',
      headerName: '操作',
      width: 160,
      sortable: false,
      renderCell: (params) => {
        const isPast = isPastTournament(params.row?.date);
        return params?.row ? (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => {
                setSelectedTournament(params.row || null);
                setDetailModalOpen(true);
              }}
              sx={{
                color: isPast ? 'rgba(0, 170, 255, 0.5)' : '#00aaff',
                padding: '4px',
                '&:hover': {
                  color: '#00ffff',
                  background: 'rgba(0, 255, 255, 0.1)',
                  filter: 'drop-shadow(0 0 8px #00ffff)',
                }
              }}
              title="詳細"
            >
              <InfoIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => navigate(`/admin/tournaments/edit/${params.row?.id}`)}
              sx={{
                color: isPast ? 'rgba(255, 0, 255, 0.5)' : '#ff00ff',
                padding: '4px',
                '&:hover': {
                  color: '#ff66ff',
                  background: 'rgba(255, 0, 255, 0.1)',
                  filter: 'drop-shadow(0 0 8px #ff00ff)',
                }
              }}
              title="編集"
            >
              <EditIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDuplicate(params.row)}
              sx={{
                color: isPast ? 'rgba(0, 255, 0, 0.5)' : '#00ff00',
                padding: '4px',
                '&:hover': {
                  color: '#66ff66',
                  background: 'rgba(0, 255, 0, 0.1)',
                  filter: 'drop-shadow(0 0 8px #00ff00)',
                }
              }}
              title="複製"
            >
              <ContentCopyIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row?.id)}
              sx={{
                color: isPast ? 'rgba(255, 0, 102, 0.5)' : '#ff0066',
                padding: '4px',
                '&:hover': {
                  color: '#ff3388',
                  background: 'rgba(255, 0, 102, 0.1)',
                  filter: 'drop-shadow(0 0 8px #ff0066)',
                }
              }}
              title="削除"
            >
              <DeleteIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Box>
        ) : null;
      },
    },
  ];

  const handleDelete = async (id) => {
    if (!id) return;
    if (!window.confirm('この大会を削除してもよろしいですか？')) return;
    
    try {
      await tournamentService.deleteTournament(id);
      setTournaments(prev => 
        prev ? prev.filter(t => t.id !== id) : null
      );
    } catch (error) {
      console.error('トーナメントの削除に失敗:', error);
      alert('トーナメントの削除に失敗しました。');
    }
  };

  const handleDuplicate = async (tournament) => {
    if (!tournament) return;
    try {
      const duplicatedData = {
        ...tournament,
        date: new Date().toISOString().split('T')[0],
        createdAt: new Date().toISOString(),
      };
      delete duplicatedData.id;
      
      const newId = await tournamentService.createTournament(duplicatedData);
      console.log('大会を複製しました:', newId);
      
      const data = await tournamentService.getAllTournaments();
      setTournaments(data);
      
      alert('大会情報を複製しました。');
    } catch (error) {
      console.error('大会の複製に失敗:', error);
      alert('大会の複製に失敗しました。');
    }
  };

  const filteredGroupedTournaments = useMemo(() => {
    if (!searchTerm) return groupedTournaments;
    
    const searchTermLower = searchTerm.toLowerCase();
    const filtered = {};
    
    Object.entries(groupedTournaments).forEach(([month, monthTournaments]) => {
      const filteredMonth = monthTournaments.filter(tournament => {
        const searchableFields = [
          tournament?.title,
          tournament?.tournamentName,
          tournament?.category,
          tournament?.venue,
          tournament?.venueLocation,
          tournament?.time,
          tournament?.broadcast?.map(b => b.platform)?.join(' '),
          tournament?.matchups?.map(m => 
            [m.round, ...(m.teams?.map(t => t.name) || [])]
          ).flat().join(' ')
        ];
        
        return searchableFields
          .filter(Boolean)
          .some(field => 
            field.toLowerCase().includes(searchTermLower)
          );
      });
      
      if (filteredMonth.length > 0) {
        filtered[month] = filteredMonth;
      }
    });
    
    return filtered;
  }, [groupedTournaments, searchTerm]);

  // 月のキーをソート（新しい月が上に）
  const sortedMonthKeys = Object.keys(filteredGroupedTournaments).sort((a, b) => b.localeCompare(a));

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3 
      }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          トーナメント一覧
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/tournaments/new')}
        >
          新規作成
        </Button>
      </Box>

      <TextField
        fullWidth
        variant="outlined"
        placeholder="タイトル、カテゴリー、会場、チーム名、時間で検索..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ 
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: 0,
            backgroundColor: 'rgba(37, 37, 48, 0.6)',
            border: '2px solid rgba(0, 255, 255, 0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(37, 37, 48, 0.8)',
              borderColor: 'rgba(0, 255, 255, 0.4)',
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(37, 37, 48, 0.9)',
              borderColor: '#00ffff',
              boxShadow: '0 0 20px rgba(0, 255, 255, 0.3)',
            },
            '& input': {
              color: '#ffffff',
              '&::placeholder': {
                color: 'rgba(255, 255, 255, 0.5)',
                opacity: 1,
              },
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#00ffff' }} />
            </InputAdornment>
          ),
          sx: {
            fontSize: '0.95rem',
            padding: '2px 0',
          },
        }}
      />

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ width: '100%' }}>
          {!Array.isArray(tournaments) || tournaments.length === 0 ? (
            <Typography sx={{ textAlign: 'center', my: 4 }}>
              {error ? 'データの読み込みに失敗しました。再度お試しください。' : 'トーナメントが登録されていません'}
            </Typography>
          ) : sortedMonthKeys.length === 0 ? (
            <Typography sx={{ textAlign: 'center', my: 4 }}>
              検索条件に一致するトーナメントが見つかりません
            </Typography>
          ) : (
            sortedMonthKeys.map(monthKey => {
              const monthTournaments = filteredGroupedTournaments[monthKey];
              const isExpanded = expandedMonths[monthKey];
              const hasPastTournaments = monthTournaments.some(t => isPastTournament(t.date));
              
              return (
                <Paper
                  key={monthKey}
                  sx={{
                    mb: 3,
                    bgcolor: 'rgba(37, 37, 48, 0.6)',
                    borderRadius: 0,
                    boxShadow: '0 0 20px rgba(0, 255, 255, 0.1)',
                    border: '2px solid rgba(0, 255, 255, 0.2)',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      bgcolor: 'rgba(0, 255, 255, 0.05)',
                      borderBottom: isExpanded ? '2px solid rgba(0, 255, 255, 0.2)' : 'none',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'rgba(0, 255, 255, 0.08)',
                      }
                    }}
                    onClick={() => toggleMonth(monthKey)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 600, 
                        color: '#00ffff',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}>
                        {getMonthDisplayName(monthKey)}
                      </Typography>
                      <Chip 
                        label={`${monthTournaments.length}件`} 
                        size="small"
                        sx={{
                          bgcolor: 'rgba(0, 255, 255, 0.2)',
                          color: '#00ffff',
                          fontWeight: 600,
                        }}
                      />
                      {hasPastTournaments && (
                        <Chip 
                          label="終了した大会を含む" 
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                            color: 'rgba(255, 255, 255, 0.5)',
                          }}
                        />
                      )}
                    </Box>
                    <IconButton sx={{ color: '#00ffff' }}>
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  
                  <Collapse in={isExpanded}>
                    <Box sx={{ p: 0 }}>
                      <DataGrid
                        rows={monthTournaments}
                        columns={columns}
                        disableSelectionOnClick
                        autoHeight
                        getRowId={(row) => row?.id}
                        hideFooter
                        density="comfortable"
                        sx={{
                          border: 'none',
                          '& .MuiDataGrid-cell': {
                            borderColor: 'rgba(0, 255, 255, 0.1)',
                            padding: '8px 12px',
                            fontSize: '0.875rem',
                            color: '#ffffff',
                          },
                          '& .MuiDataGrid-columnHeaders': {
                            bgcolor: 'rgba(0, 255, 255, 0.03)',
                            borderColor: 'rgba(0, 255, 255, 0.2)',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: '#00ffff',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          },
                          '& .MuiDataGrid-columnHeader': {
                            '&:focus': {
                              outline: 'none',
                            },
                            '&:focus-within': {
                              outline: 'none',
                            },
                          },
                          '& .MuiDataGrid-row': {
                            '&:hover': {
                              bgcolor: 'rgba(0, 255, 255, 0.05)',
                              cursor: 'pointer',
                            },
                            '&.Mui-selected': {
                              bgcolor: 'rgba(0, 255, 255, 0.1)',
                              '&:hover': {
                                bgcolor: 'rgba(0, 255, 255, 0.15)',
                              },
                            },
                          },
                          '& .MuiSvgIcon-root': {
                            color: '#00ffff',
                          },
                        }}
                      />
                    </Box>
                  </Collapse>
                </Paper>
              );
            })
          )}
        </Box>
      )}

      <TournamentDetailModal
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setTimeout(() => setSelectedTournament(null), 300);
        }}
        tournament={selectedTournament}
      />
    </Box>
  );
};

export default TournamentList;
